import { Mail, Info } from 'lucide-react';
import { EmailLog } from '@/types/email-log.types';
import EmailLogAvatar from './EmailLogAvatar';
import EmailLogStatusBadge from './EmailLogStatusBadge';
import EmptyState from '@/components/ui/EmptyState';
import { SkeletonLoader } from '@/components/ui/SkeletonLoader';

interface EmailLogListProps {
  logs: EmailLog[];
  selectedId?: string;
  onSelect: (log: EmailLog) => void;
  isLoading?: boolean;
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  if (diffDays === 1) {
    return 'Yesterday';
  }

  if (diffDays < 7) {
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

function formatFullDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

function getEmailTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    welcome: 'Welcome Email',
    password_reset: 'Password Reset',
    invitation: 'Invitation',
    reminder: 'Reminder',
    notification: 'Notification',
    certificate: 'Certificate',
    other: 'Email',
  };
  return labels[type] || 'Email';
}

function EmailLogListSkeleton() {
  return (
    <div className="divide-y divide-gray-200">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="p-4">
          <div className="flex items-start gap-3">
            <SkeletonLoader type="avatar" />
            <div className="flex-1 min-w-0 space-y-2">
              <div className="flex items-center justify-between">
                <SkeletonLoader type="text" width="8rem" />
                <SkeletonLoader type="text" width="4rem" />
              </div>
              <div className="flex items-center gap-2">
                <SkeletonLoader type="text" width="10rem" />
                <SkeletonLoader type="text" width="4rem" height="1.25rem" className="rounded-full" />
              </div>
              <SkeletonLoader type="text" width="100%" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function EmailLogList({
  logs,
  selectedId,
  onSelect,
  isLoading,
}: EmailLogListProps) {
  if (isLoading) {
    return <EmailLogListSkeleton />;
  }

  if (logs.length === 0) {
    return (
      <EmptyState
        icon={Mail}
        title="No email logs found"
        description="Try adjusting your filters or search criteria"
      />
    );
  }

  return (
    <div className="divide-y divide-gray-200">
      {logs.map((log) => {
        const isSelected = selectedId === log._id;

        return (
          <button
            key={log._id}
            onClick={() => onSelect(log)}
            className={`w-full text-left p-4 transition-all hover:bg-gray-50 ${
              isSelected
                ? 'bg-blue-50 border-l-4 border-l-blue-600'
                : 'border-l-4 border-l-transparent'
            }`}
          >
            <div className="flex items-start gap-3">
              <EmailLogAvatar
                name={log.recipient_name || log.recipient_email}
                email={log.recipient_email}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-sm font-semibold text-gray-900 truncate">
                    {log.recipient_name || 'No Name'}
                  </span>
                  <span className="text-xs text-gray-500 flex-shrink-0 ml-2">
                    {formatDate(log.created_at)}
                  </span>
                </div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs text-gray-500 truncate">
                    {log.recipient_email}
                  </span>
                  <EmailLogStatusBadge status={log.status} size="sm" />
                  {log.from_archive && (
                    <span className="inline-flex items-center rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500 flex-shrink-0">
                      Archived
                    </span>
                  )}
                  {log.error_message && (
                    <span title={log.error_message}>
                      <Info className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-600 truncate">
                  {log.subject || `To: ${log.recipient_email}`}
                </p>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

export { formatFullDate, getEmailTypeLabel };
